import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS, SKILLS, REGIONS, PREAMBLE, DECLARATION } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Check, ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = ["Account & Identity", "Contributory Skills", "Committee Interests", "Preamble & Oath"];

const STEP_MOTION = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.3 },
};

function stepCircleClass(i, step) {
  if (i < step) return "bg-[#007A33] border-[#007A33] text-white";
  if (i === step) return "bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]";
  return "border-[#F5F3EF]/20 text-[#F5F3EF]/40";
}

function FieldInput({ label, ...props }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">{label}</Label>
      <Input {...props}
        className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0 text-lg" />
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    email: "", password: "", full_name: "", date_of_birth: "",
    place_of_birth: "", residence: "", region: "",
    skills: [], other_skills: "",
    committee_interests: [], desired_committees: "",
    preamble_agreed: false, declaration_signed: false, signature_name: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSkill = (s) =>
    setForm((f) => ({ ...f, skills: f.skills.includes(s) ? f.skills.filter((x) => x !== s) : [...f.skills, s] }));
  const toggleInterest = (s) =>
    setForm((f) => ({ ...f, committee_interests: f.committee_interests.includes(s) ? f.committee_interests.filter((x) => x !== s) : [...f.committee_interests, s] }));

  const validateStep = () => {
    setError("");
    if (step === 0) {
      if (!form.email || !form.password || !form.full_name || !form.date_of_birth || !form.place_of_birth || !form.residence) {
        setError("Please complete all identity fields."); return false;
      }
      if (form.password.length < 6) { setError("Password must be at least 6 characters."); return false; }
    }
    if (step === 3) {
      if (!form.preamble_agreed) { setError("You must affirm the Preamble to proceed."); return false; }
      if (!form.declaration_signed) { setError("You must agree to the Declaration of Membership."); return false; }
      if (!form.signature_name.trim()) { setError("Please type your full legal name as your digital signature."); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 3)); };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError("");
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="tribal-stripe h-2 w-full" />
      <div className="max-w-4xl mx-auto px-6 sm:px-12 py-12">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src={ASSETS.seal} alt="Seal" className="h-12 w-12 object-contain" />
          <div className="font-serif-display text-lg font-semibold text-[#F5F3EF]">The Black American Government</div>
        </Link>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-12">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-semibold transition-colors ${stepCircleClass(i, step)}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className="hidden sm:block text-[10px] tracking-wider uppercase mt-2 text-center w-24 text-[#A8A29E]">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-[#007A33]" : "bg-[#F5F3EF]/15"}`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="bg-[#C8102E]/15 border border-[#C8102E]/40 text-[#F5A3A3] px-4 py-3 mb-6 text-sm" data-testid="register-error">{error}</div>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} {...STEP_MOTION}>
            <div className="bg-[#141414] border border-[#D4AF37]/30 p-8 sm:p-12">
              {step === 0 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-serif-display text-3xl text-[#F5F3EF]">Account & Identity</h2>
                    <p className="text-[#A8A29E] mt-1">Establish your credentials and record your personal identity.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <FieldInput label="Email Address" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email-input" />
                    <FieldInput label="Password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password-input" />
                    <FieldInput label="Full Legal Name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="reg-fullname-input" />
                    <FieldInput label="Date of Birth" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="reg-dob-input" />
                    <FieldInput label="Location of Birth" placeholder="City, State / Country" value={form.place_of_birth} onChange={(e) => set("place_of_birth", e.target.value)} data-testid="reg-pob-input" />
                    <FieldInput label="Current Residence" placeholder="City, State" value={form.residence} onChange={(e) => set("residence", e.target.value)} data-testid="reg-residence-input" />
                    <div>
                      <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Region</Label>
                      <Select value={form.region} onValueChange={(v) => set("region", v)}>
                        <SelectTrigger className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/20 bg-transparent text-[#F5F3EF] focus:ring-0 px-0 text-lg" data-testid="reg-region-select">
                          <SelectValue placeholder="Select your region" />
                        </SelectTrigger>
                        <SelectContent>
                          {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-serif-display text-3xl text-[#F5F3EF]">Contributory Skills</h2>
                    <p className="text-[#A8A29E] mt-1">Select every area in which you can contribute to the Nation.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SKILLS.map((s) => {
                      const active = form.skills.includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggleSkill(s)}
                          className={`flex items-center gap-3 border-2 px-4 py-3 text-left transition-colors ${active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F5F3EF]" : "border-[#F5F3EF]/15 text-[#D6D2CA] hover:border-[#F5F3EF]/40"}`}
                          data-testid={`reg-skill-${s.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
                          <span className={`w-5 h-5 border-2 flex items-center justify-center ${active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#F5F3EF]/30"}`}>
                            {active && <Check className="w-3 h-3 text-[#0A0A0A]" />}
                          </span>
                          <span className="text-sm font-medium">{s}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Other Skills & Experience</Label>
                    <Textarea value={form.other_skills} onChange={(e) => set("other_skills", e.target.value)} placeholder="Describe any additional expertise, certifications, or experience..."
                      className="mt-2 rounded-none border-2 border-[#F5F3EF]/15 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37]" data-testid="reg-other-skills-input" />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-serif-display text-3xl text-[#F5F3EF]">Committee Interests</h2>
                    <p className="text-[#A8A29E] mt-1">Express the committees and programs you wish to join or build.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {["Justice & Legal Affairs", "Health & Wellness", "Media & Communications", "Agriculture & Land Stewardship", "Education & Heritage", "Economic Development"].map((s) => {
                      const active = form.committee_interests.includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggleInterest(s)}
                          className={`flex items-center gap-3 border-2 px-4 py-3 text-left transition-colors ${active ? "border-[#5DBB7A] bg-[#5DBB7A]/10 text-[#F5F3EF]" : "border-[#F5F3EF]/15 text-[#D6D2CA] hover:border-[#F5F3EF]/40"}`}
                          data-testid={`reg-interest-${s.split(" ")[0].toLowerCase()}`}>
                          <span className={`w-5 h-5 border-2 flex items-center justify-center ${active ? "bg-[#5DBB7A] border-[#5DBB7A]" : "border-[#F5F3EF]/30"}`}>
                            {active && <Check className="w-3 h-3 text-[#0A0A0A]" />}
                          </span>
                          <span className="text-sm font-medium">{s}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Desire to Create New Committees or Programs</Label>
                    <Textarea value={form.desired_committees} onChange={(e) => set("desired_committees", e.target.value)} placeholder="Describe any new committee or program you would like to establish within the Nation..."
                      className="mt-2 rounded-none border-2 border-[#F5F3EF]/15 bg-transparent text-[#F5F3EF] focus-visible:ring-0 focus-visible:border-[#D4AF37]" data-testid="reg-desired-committees-input" />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-serif-display text-3xl text-[#F5F3EF]">Preamble & Declaration</h2>
                    <p className="text-[#A8A29E] mt-1">Read and affirm the founding charter and membership oath.</p>
                  </div>

                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-3">The Preamble</div>
                    <div className="doc-scroll border border-[#D4AF37]/30 p-6 max-h-52 overflow-y-auto font-serif-display text-base leading-relaxed space-y-4 text-[#EDE9E1]" data-testid="reg-preamble-text">
                      {PREAMBLE.split("\n\n").map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
                    </div>
                    <label className="flex items-start gap-3 mt-4 cursor-pointer">
                      <Checkbox checked={form.preamble_agreed} onCheckedChange={(v) => set("preamble_agreed", !!v)} className="mt-1 rounded-none border-[#F5F3EF]/40 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" data-testid="reg-preamble-checkbox" />
                      <span className="text-sm text-[#A8A29E]">I have read and affirm the Preamble of the Indigenous Black American Tribal Nation.</span>
                    </label>
                  </div>

                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-3">Declaration of Membership</div>
                    <div className="doc-scroll border border-[#D4AF37]/30 p-6 max-h-52 overflow-y-auto font-serif-display text-base leading-relaxed space-y-4 text-[#EDE9E1]" data-testid="reg-declaration-text">
                      {DECLARATION.split("\n\n").map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
                    </div>
                    <label className="flex items-start gap-3 mt-4 cursor-pointer">
                      <Checkbox checked={form.declaration_signed} onCheckedChange={(v) => set("declaration_signed", !!v)} className="mt-1 rounded-none border-[#F5F3EF]/40 data-[state=checked]:bg-[#5DBB7A] data-[state=checked]:border-[#5DBB7A]" data-testid="reg-declaration-checkbox" />
                      <span className="text-sm text-[#A8A29E]">I, of sound mind and acting freely, agree to and digitally sign this Declaration of Membership.</span>
                    </label>
                  </div>

                  <div className="border-t border-[#F5F3EF]/10 pt-6">
                    <Label className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">Digital Signature — Type Your Full Legal Name</Label>
                    <Input value={form.signature_name} onChange={(e) => set("signature_name", e.target.value)} placeholder="Your full legal name"
                      className="mt-2 rounded-none border-0 border-b-2 border-[#F5F3EF]/30 bg-transparent focus-visible:ring-0 focus-visible:border-[#D4AF37] px-0 text-2xl font-serif-display italic text-[#D4AF37]"
                      data-testid="reg-signature-input" />
                    <p className="text-xs text-[#A8A29E] mt-2">Executed this {new Date().toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}.</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-none text-[#A8A29E] hover:text-[#F5F3EF] hover:bg-transparent" data-testid="reg-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {step < 3 ? (
            <Button onClick={next} className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8" data-testid="reg-next-btn">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={loading} className="rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8" data-testid="reg-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Membership"}
            </Button>
          )}
        </div>

        <p className="text-center text-sm text-[#A8A29E] mt-8">
          Already a member? <Link to="/login" className="text-[#D4AF37] font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
